export const ANSWER_REVEAL_MODE = {
  ALWAYS: 'always',
  INCORRECT_ONLY: 'incorrect_only',
  NEVER: 'never',
} as const;

export type AnswerRevealMode =
  (typeof ANSWER_REVEAL_MODE)[keyof typeof ANSWER_REVEAL_MODE];

export const ANSWER_REVEAL_MODE_OPTIONS: AnswerRevealMode[] = [
  ANSWER_REVEAL_MODE.NEVER,
  ANSWER_REVEAL_MODE.INCORRECT_ONLY,
  ANSWER_REVEAL_MODE.ALWAYS,
];

/**
 * Round-battle 템플릿(templateType) 식별자. 라운드전은 정답을 항상 보여주기로 강제된다.
 */
export const ROUND_TEMPLATE_TYPE = 'round';

export function resolveAnswerRevealMode({
  answerRevealMode,
  showIncorrectAnswer,
}: {
  answerRevealMode?: AnswerRevealMode | null;
  showIncorrectAnswer?: boolean | null;
}): AnswerRevealMode {
  if (answerRevealMode) return answerRevealMode;
  return showIncorrectAnswer
    ? ANSWER_REVEAL_MODE.INCORRECT_ONLY
    : ANSWER_REVEAL_MODE.NEVER;
}

export function toLegacyShowIncorrectAnswer(
  answerRevealMode: AnswerRevealMode,
): boolean {
  return answerRevealMode !== ANSWER_REVEAL_MODE.NEVER;
}

export function resolveBuildAnswerRevealMode({
  answerRevealMode,
  showIncorrectAnswer,
  templateType,
}: {
  answerRevealMode?: AnswerRevealMode | null;
  showIncorrectAnswer?: boolean | null;
  templateType?: string | null;
}): AnswerRevealMode {
  if (templateType === ROUND_TEMPLATE_TYPE) return ANSWER_REVEAL_MODE.ALWAYS;
  return resolveAnswerRevealMode({ answerRevealMode, showIncorrectAnswer });
}
export function resolvePlayAnswerRevealMode({
  answerRevealMode,
  delayAnswerShow,
}: {
  answerRevealMode: AnswerRevealMode;
  delayAnswerShow?: boolean | null;
}): AnswerRevealMode {
  return delayAnswerShow ? ANSWER_REVEAL_MODE.NEVER : answerRevealMode;
}

export function shouldRevealCorrectAnswerOnPass(
  answerRevealMode: AnswerRevealMode,
): boolean {
  return answerRevealMode === ANSWER_REVEAL_MODE.ALWAYS;
}

export function shouldRevealIncorrectAnswerOnPass(
  answerRevealMode: AnswerRevealMode,
): boolean {
  return answerRevealMode !== ANSWER_REVEAL_MODE.NEVER;
}
export function shouldShowEssaySubmittedFeedback(
  answerRevealMode: AnswerRevealMode,
): boolean {
  return answerRevealMode === ANSWER_REVEAL_MODE.ALWAYS;
}

export type AnswerRevealModeLabelKey =
  | 'detail.showAnswerAlways'
  | 'detail.showAnswerIncorrectOnly'
  | 'detail.notShowAnswer';

export type BuildAnswerRevealModeLabelKey =
  | 'answerReveal.optionAlways'
  | 'answerReveal.optionIncorrectOnly'
  | 'answerReveal.optionNever';

/**
 * DETAIL 배지(ShowAnswerBadge)용 i18n 키. `common` 네임스페이스에서 번역한다.
 *
 * 리터럴 유니온을 반환해 typed `t()` 가 키를 그대로 좁혀 검증하게 한다(plain string 반환 시
 * `t()` 가 unknown 오버로드로 빠져 다운스트림 타입이 깨진다).
 */
export function getAnswerRevealModeLabelKey(
  answerRevealMode: AnswerRevealMode,
): AnswerRevealModeLabelKey {
  switch (answerRevealMode) {
    case ANSWER_REVEAL_MODE.ALWAYS:
      return 'detail.showAnswerAlways';
    case ANSWER_REVEAL_MODE.INCORRECT_ONLY:
      return 'detail.showAnswerIncorrectOnly';
    case ANSWER_REVEAL_MODE.NEVER:
      return 'detail.notShowAnswer';
  }
}
export function getAnswerRevealModeLabel(
  answerRevealMode: AnswerRevealMode,
): string {
  switch (answerRevealMode) {
    case ANSWER_REVEAL_MODE.ALWAYS:
      return '정답 항상 표시';
    case ANSWER_REVEAL_MODE.INCORRECT_ONLY:
      return '틀렸을 때 정답 표시';
    case ANSWER_REVEAL_MODE.NEVER:
      return '정답 미표시';
  }
}
/**
 * BUILD 폼(AnswerRevealModeField) 셀렉트 옵션용 i18n 키. `build` 네임스페이스에서 번역한다.
 *
 * 리터럴 유니온을 반환해 typed `t()` 가 키를 그대로 좁혀 검증하게 한다(plain string 반환 시
 * `t()` 가 unknown 오버로드로 빠져 다운스트림 타입이 깨진다).
 */
export function getBuildAnswerRevealModeLabelKey(
  answerRevealMode: AnswerRevealMode,
): BuildAnswerRevealModeLabelKey {
  switch (answerRevealMode) {
    case ANSWER_REVEAL_MODE.ALWAYS:
      return 'answerReveal.optionAlways';
    case ANSWER_REVEAL_MODE.INCORRECT_ONLY:
      return 'answerReveal.optionIncorrectOnly';
    case ANSWER_REVEAL_MODE.NEVER:
      return 'answerReveal.optionNever';
  }
}
export function getBuildAnswerRevealModeLabel(
  answerRevealMode: AnswerRevealMode,
): string {
  switch (answerRevealMode) {
    case ANSWER_REVEAL_MODE.ALWAYS:
      return '항상 보여주기';
    case ANSWER_REVEAL_MODE.INCORRECT_ONLY:
      return '틀렸을 때만 보여주기';
    case ANSWER_REVEAL_MODE.NEVER:
      return '보여주지 않기';
  }
}
